import DOMPurify from 'dompurify';

const HTML_CONTENT_PATTERN = /<(?:p|div|h[1-6]|ul|ol|li|blockquote|a|img|iframe|video|source|strong|em|u|s|code|pre|br|hr)\b/i;
const PLAIN_TEXT_BLOCK_END_PATTERN = /<\/(?:p|div|h[1-6]|li|blockquote|pre|ul|ol)\s*>/gi;
const PLAIN_TEXT_BREAK_PATTERN = /<br\s*\/?>/gi;
const HTML_ENTITY_PATTERN = /&(?:#\d+|#x[0-9a-f]+|[a-z][a-z0-9]+);/i;
const MAX_ENTITY_DECODE_PASSES = 4;

const decodeHtmlEntitiesOnce = (value = '') => {
  const source = String(value || '');
  if (!HTML_ENTITY_PATTERN.test(source)) return source;

  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = source;
    return textarea.value;
  }

  return source
    .replace(/&#x([0-9a-f]+);/gi, (_match, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_match, decimal) => String.fromCodePoint(parseInt(decimal, 10)))
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&');
};

// Rich-text translations can be returned entity-encoded (sometimes more than once).
// Normalize first so every locale follows the same sanitizing/rendering path.
export const normalizeRichTextSource = (value = '') => {
  let normalized = String(value || '');

  for (let pass = 0; pass < MAX_ENTITY_DECODE_PASSES; pass += 1) {
    const decoded = decodeHtmlEntitiesOnce(normalized);
    if (decoded === normalized) break;
    normalized = decoded;
  }

  return normalized;
};

const VIDEO_SANITIZE_OPTIONS = {
  ADD_TAGS: ['iframe', 'video', 'source'],
  ADD_ATTR: [
    'allow',
    'allowfullscreen',
    'controls',
    'playsinline',
    'preload',
    'poster',
    'referrerpolicy',
    'title',
  ],
  FORBID_ATTR: ['srcdoc'],
};

const isTrustedEmbedUrl = (value) => {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (host === 'www.youtube-nocookie.com' || host === 'www.youtube.com') {
      return /^\/embed\/[a-zA-Z0-9_-]{6,20}\/?$/.test(url.pathname);
    }
    if (host === 'player.vimeo.com') {
      return /^\/video\/\d{5,15}\/?$/.test(url.pathname);
    }
    if (host === 'www.aparat.com') {
      return /^\/video\/video\/embed\/videohash\/[a-zA-Z0-9_-]{3,32}\/vt\/frame\/?$/.test(url.pathname);
    }
    return false;
  } catch (_) {
    return false;
  }
};

const isSafeMediaUrl = (value) => {
  const source = String(value || '').trim();
  if (/^\/(?!\/)/.test(source)) return true;
  try {
    return ['http:', 'https:'].includes(new URL(source).protocol);
  } catch (_) {
    return false;
  }
};

export const sanitizeRichText = (value = '', { allowVideo = false } = {}) => DOMPurify.sanitize(
  normalizeRichTextSource(value),
  {
    ...(allowVideo ? VIDEO_SANITIZE_OPTIONS : {}),
    ADD_ATTR: allowVideo ? ['target', ...VIDEO_SANITIZE_OPTIONS.ADD_ATTR] : ['target'],
    ...(!allowVideo ? { FORBID_TAGS: ['iframe', 'video', 'source'] } : {}),
  }
);

export const richTextToPlainText = (value = '') => {
  // Add spaces at block boundaries before removing HTML. Without this, e.g.
  // </h2><p> would join the heading and paragraph text together.
  const source = normalizeRichTextSource(value)
    .replace(PLAIN_TEXT_BREAK_PATTERN, ' ')
    .replace(PLAIN_TEXT_BLOCK_END_PATTERN, (match) => ` ${match}`);

  const cleanText = DOMPurify.sanitize(source, { ALLOWED_TAGS: [] });

  if (typeof document === 'undefined') {
    return cleanText
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const container = document.createElement('div');
  container.innerHTML = cleanText;
  return (container.textContent || '').replace(/\s+/g, ' ').trim();
};

export const hasRichTextContent = (value = '', { allowVideo = false } = {}) => {
  const sanitized = sanitizeRichText(value, { allowVideo });
  return richTextToPlainText(sanitized).length > 0
    || /<img\b/i.test(sanitized)
    || (allowVideo && /<(?:iframe|video)\b|data-article-video/i.test(sanitized));
};

export const prepareRichTextForDisplay = (value = '', apiUrl = '', { allowVideo = false } = {}) => {
  const source = normalizeRichTextSource(value);
  const safeHtml = sanitizeRichText(
    HTML_CONTENT_PATTERN.test(source) ? source : source.replace(/\n/g, '<br>'),
    { allowVideo }
  );

  if (typeof document === 'undefined') return safeHtml;

  const container = document.createElement('div');
  container.innerHTML = safeHtml;
  if (apiUrl) {
    container.querySelectorAll('img[src^="/"], video[src^="/"], source[src^="/"]').forEach((media) => {
      media.setAttribute('src', `${apiUrl}${media.getAttribute('src')}`);
    });
  }

  if (allowVideo) {
    container.querySelectorAll('iframe').forEach((iframe) => {
      if (!isTrustedEmbedUrl(iframe.getAttribute('src'))) {
        const wrapper = iframe.closest('[data-article-video]');
        if (wrapper) wrapper.remove();
        else iframe.remove();
        return;
      }
      iframe.setAttribute('loading', 'lazy');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    });

    container.querySelectorAll('video, source').forEach((media) => {
      if (!isSafeMediaUrl(media.getAttribute('src'))) {
        const wrapper = media.closest('[data-article-video]');
        if (wrapper) wrapper.remove();
        else media.remove();
        return;
      }
      if (media.tagName === 'VIDEO') {
        media.setAttribute('controls', '');
        media.setAttribute('playsinline', '');
        media.setAttribute('preload', 'metadata');
      }
    });

    container.querySelectorAll('[data-article-video]').forEach((wrapper) => {
      if (!wrapper.querySelector('iframe, video')) wrapper.remove();
    });
  }
  return container.innerHTML;
};
